import { useEffect, useState } from "react";
import Box from "@mui/material/Box";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import PrepaymentFormData from "~/store/PrepaymentFormData.js";
import { getAllBrasilStates } from "~/utils/advanceRequest";
import SmartField from "~/features/doctorForm/SmartField";
import { isValidCpf, isValidEmail, isValidFullName, onlyDigits } from "~/features/doctorForm/masks";
import { FormText, FormTitle, SectionLabel } from "~/features/doctorForm/FormLayout";
import { CONDITIONS_TITLE } from "./Step2";

const STATES = getAllBrasilStates();
const SETTERS = { email: "setEmail", fullName: "setFullName", cpf: "setCPF", crm: "setCRM", crmuf: "setCRMUF", acceptTermsData: "setAcceptTermsData" };

const Step3 = ({ setDisabledNext, mobile }) => {
  const d = PrepaymentFormData.data;
  // CPF e CRM guardam só dígitos no store (a máscara é só visual).
  const [form, setForm] = useState(() => ({
    email: d.email,
    fullName: d.fullName,
    cpf: onlyDigits(d.cpf),
    crm: d.crm,
    crmuf: d.crmuf,
    acceptTermsData: d.acceptTermsData,
  }));
  const valid =
    // Normaliza antes de validar (G1.12): com o foco ainda no e-mail, Continuar já fica liberado.
    isValidEmail(String(form.email ?? "").trim().toLowerCase()) &&
    isValidFullName(form.fullName) &&
    isValidCpf(form.cpf) &&
    form.crm !== "" &&
    STATES.some((s) => s.uf === form.crmuf) &&
    form.acceptTermsData;

  useEffect(() => {
    setDisabledNext(!valid);
  }, [valid, setDisabledNext]);

  const update = (field) => (value) => {
    PrepaymentFormData[SETTERS[field]](value);
    setForm((f) => ({ ...f, [field]: value }));
  };

  return (
    <Box>
      <FormTitle mobile={mobile}>{CONDITIONS_TITLE}</FormTitle>
      <SectionLabel sx={{ mt: 3 }}>Dados Pessoais</SectionLabel>
      <FormText sx={{ mt: 1, color: "text.secondary" }}>
        <b style={{ color: "#1f2937" }}>Termo de Permissão e Concordância</b> para Compartilhamento de Dados Pessoais de Identificação que foram cedidos por mim
        anteriormente as empresas em que realizei plantões e todas as informações financeiras decorrentes de minhas atividades nas empresas
      </FormText>
      <Box sx={{ mt: 3, display: "grid", gap: 2, gridTemplateColumns: mobile ? "minmax(0, 1fr)" : "minmax(0, 1fr) minmax(0, 1fr)" }}>
        <SmartField kind="email" value={form.email} onValue={update("email")} sx={{ gridColumn: "1 / -1" }} />
        <SmartField kind="name" value={form.fullName} onValue={update("fullName")} sx={{ gridColumn: "1 / -1" }} />
        <SmartField kind="cpf" value={form.cpf} onValue={update("cpf")} sx={{ gridColumn: "1 / -1" }} />
        <SmartField kind="crm" value={form.crm} onValue={update("crm")} />
        <TextField
          select
          fullWidth
          label="Escolha o estado do CRM"
          value={form.crmuf || ""}
          onChange={(e) => update("crmuf")(e.target.value)}
          helperText="Estado onde o CRM foi emitido"
          SelectProps={{ MenuProps: { PaperProps: { sx: { maxHeight: 320 } } } }}
        >
          {STATES.map((s) => (
            <MenuItem key={s.uf} value={s.uf}>
              {s.name}
            </MenuItem>
          ))}
        </TextField>
      </Box>
      <Box sx={{ mt: 1, p: 2, borderRadius: 2, bgcolor: "#f5f7fa", border: "1px solid #e5e7eb" }}>
        <FormText sx={{ fontSize: 14 }}>
          Este <b>Termo</b> tem como propósito compartilhar todos os <b>DADOS</b> DE <b>IDENTIFICAÇÃO PESSOAL</b> (Nome Completo, RG, CPF, Estado Civil,
          Profissão, Endereço, Dados Bancários e Foto de Identificação) que já foram cedidos por mim anteriormente as empresas em que realizei plantões, além de
          todas as INFORMAÇÕES FINANCEIRAS decorrentes de minha atuação junto à referida as empresa.
        </FormText>
        <FormControlLabel
          sx={{ mt: 1, alignItems: "flex-start", "& .MuiCheckbox-root": { mt: -0.75 } }}
          control={<Checkbox checked={form.acceptTermsData} onChange={(e) => update("acceptTermsData")(e.target.checked)} />}
          label={<b>Sim, estou de acordo. Permito o compartilhamento destes dados com a ANTECIPE PAGAMENTOS.</b>}
        />
      </Box>
    </Box>
  );
};

export default Step3;
